import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { http } from "../api/http";
import { useAuthStore } from "../store/auth";
export type NoteExtrasData = {
  files?: Array<{ id: string; name: string; size: number }>;
  mentions?: Array<{ id: string; firstName: string; lastName: string }>;
  resolvedReferences?: Array<{
    entityType: string;
    entityId: string;
    available: boolean;
    label?: string;
  }>;
};
export function TeamNoteExtras({
  id,
  data,
}: {
  id: string;
  data: NoteExtrasData;
}) {
  const [error, setError] = useState("");
  async function download(file: NonNullable<NoteExtrasData["files"]>[number]) {
    try {
      setError("");
      const { data: blob } = await http.get<Blob>(
        `/v1/workspace/notes/${id}/files/${file.id}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setError("Pièce jointe indisponible ou partage retiré.");
    }
  }
  return (
    <div className="space-y-3">
      {!!data.mentions?.length && (
        <p>
          Personnes mentionnées :{" "}
          {data.mentions.map((m) => m.firstName + " " + m.lastName).join(", ")}
        </p>
      )}

      {!!data.files?.length && (
        <div>
          <h3 className="font-semibold">Pièces jointes</h3>
          {data.files.map((f) => (
            <button
              key={f.id}
              className="rounded-xl border px-4 py-2 mr-2"
              onClick={() => void download(f)}
            >
              {f.name} · {Math.ceil(f.size / 1024)} Ko
            </button>
          ))}
        </div>
      )}
      {error && <p role="alert">{error}</p>}
    </div>
  );
}
export function TeamNoteNotifications({
  onOpen,
}: {
  onOpen: (id: string) => void;
}) {
  const user = useAuthStore((s) => s.user?.id);
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");
  const q = useQuery({
    queryKey: ["team-note-notifications", user, page],
    queryFn: async () =>
      (
        await http.get<{
          items: Array<{
            id: string;
            noteId: string;
            title: string;
            read: boolean;
          }>;
          hasMore: boolean;
        }>("/v1/workspace/notes/notifications", { params: { page } })
      ).data,
    refetchInterval: 15000,
    gcTime: 0,
  });
  return (
    <section className="rounded-xl border bg-white p-4 space-y-3">
      <h2 className="font-semibold">Mes mentions</h2>
      {q.isError ? (
        <p role="alert">Notifications indisponibles.</p>
      ) : (
        !q.isFetching &&
        q.data?.items?.map((n) => (
          <button
            className="block rounded-xl border px-4 py-2"
            key={n.id}
            onClick={() => {
              void http
                .post("/v1/workspace/notes/notifications/" + n.id + "/read")
                .then(() => {
                  setError("");
                  onOpen(n.noteId);
                  void q.refetch();
                })
                .catch(() => {
                  setError("Notification indisponible ou partage retiré.");
                  void q.refetch();
                });
            }}
          >
            {n.read ? "" : "Non lue · "}
            {n.title}
          </button>
        ))
      )}
      {q.data?.items?.length === 0 && <p>Aucune mention.</p>}
      {error && <p role="alert">{error}</p>}
      <div className="flex gap-3">
        <button disabled={page === 1} onClick={() => setPage(page - 1)}>
          Mentions précédentes
        </button>
        <button disabled={!q.data?.hasMore} onClick={() => setPage(page + 1)}>
          Mentions suivantes
        </button>
      </div>
    </section>
  );
}
